<template>
    <div class="operationsstatistik">
        <div class="header">
            <h1>Operationsstatistik</h1>
        </div>
        <search-filter
            v-model="filterValues"
            @input="handleSearch"
            @filterUpdated="handleFilterValuesUpdated"
            :fields="filterFields"
            :isOpen="true"
            :enableSubmit="true"
        >
            <template v-slot:header-filters>
                <FormulateInput class="search-field" name="q" placeholder="Sök operationen" />
            </template>
            <template v-slot:fields>
                <FormulateInput type="datepicker" name="date_from" label="Datum fr.o.m." />
                <FormulateInput type="datepicker" name="date_to" label="Datum t.o.m." />
                <FormulateInput type="vueselect" name="operationsenhet" label="Enhet" :options="operationsenheter" />
                <FormulateInput type="vueselect" name="planerad_vardform" label="Vårdform" :options="vardformer" />
                <FormulateInput type="vueselect" name="kontaktorsak" label="Kontaktorsak" :options="kontaktorsaker" />
                <FormulateInput type="vueselect" name="bokad_sal__id" label="Opsal" :options="salar" />
                <FormulateInput
                    type="vueselect"
                    name="current_huvudoperator"
                    label="Huvudoperatör"
                    :options="operatorer"
                />
                <FormulateInput type="vueselect" name="avtal" label="Avtal" :options="avtal" />
                <FormulateInput
                    type="vueselect"
                    name="current_typoperation"
                    label="Typoperation"
                    :options="typoperationer"
                />
                <HuvudoperationskodAutocomplete name="current_huvudoperationskod" />

                <FormulateInput type="vueselect" name="status" label="Status" :options="statusar" />
                <FormulateInput type="number" name="antal_nivaer" label="Antal nivåer" min="1" step="1" />
                <FormulateInput
                    type="vueselect"
                    name="forsakringsbolag"
                    label="Försäkringsbolag"
                    :options="forsakringsbolag"
                />
                <FormulateInput type="vueselect" name="anestesiolog" label="Anestesiolog" :options="anestesiologer" />
                <FormulateInput
                    type="checkbox"
                    name="show_antal_nivaer_null"
                    label="Visa operationer utan sparade nivåer"
                />
                <FormulateInput type="checkbox" name="show_person_id" label="Visa PersonID" />
            </template>
        </search-filter>

        <div v-if="showSummary">
            <OperationsSummary
                :summary-table-data="operationsSummaryData.summaryTableData"
                :total-operations="operationsSummaryData.totalOperations"
                :total-operation-time="operationsSummaryData.totalOperationTime"
                :is-loading-genomford-data="isLoadingGenomfordData"
            />
            <ButtonGroup>
                <Button
                    type="button"
                    role="button"
                    size="secondary"
                    @click="fetchOperationsStatistik"
                    :disabled="buttonShouldBeDisabled"
                    v-tooltip="{
                        content: buttonShouldBeDisabled ? disabledButtonTooltip : '',
                        theme: 'kliniken-tooltip',
                    }"
                >
                    <img src="@/assets/loader_white.svg" />Generera tabell
                </Button>
                <Button
                    type="button"
                    role="button"
                    size="secondary"
                    @click="downloadExcelFile"
                    :disabled="buttonShouldBeDisabled"
                    v-tooltip="{
                        content: buttonShouldBeDisabled ? disabledButtonTooltip : '',
                        theme: 'kliniken-tooltip',
                    }"
                >
                    <img src="@/assets/download_white.svg" />Exportera excel
                </Button>
            </ButtonGroup>
        </div>

        <div v-if="showTable">
            <div class="subheader">
                <span>
                    Visar <strong>{{ items.length }}</strong> av <strong>{{ count }}</strong> träffar
                </span>
            </div>

            <kliniken-table
                :columns="columns"
                :items="items"
                :count="count"
                paginationStyle="paging"
                @loadPage="loadPage"
                :currentPage="currentPage"
                @changeLimit="changeLimit"
                :limit="limit"
                @sort="sort"
                :hasDropdown="true"
                :dropdownActions="dropdownActions"
                @tableRowClicked="tableRowClicked"
                :selectedItem="selectedItem"
                @visaPreanestesibedomning="visaPreanestesibedomning"
                @visaOperationsinformation="visaOperationsinformation"
            />
        </div>
    </div>
</template>

<script>
    import tabIcon from "@/assets/operationsstatistik.svg";

    export default {
        name: "Operationsstatistik",
        tabName: "Operationsstatistik",
        icon: tabIcon,
    };
</script>

<script setup>
    import { computed, ref, onMounted, onUnmounted, toRefs } from "vue";
    import isEqual from "lodash/isEqual";
    import { getConfig, klinikenApi, getErrorMessage } from "@/api";
    import { openDialog } from "@/utils";
    import { profileOptions } from "@/tabs/Operation/utils";
    import store from "@/store";
    import SearchFilter from "@/components/SearchFilter.vue";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import OperationsSummary from "@/components/OperationsSummary.vue";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import Button from "@/components/Button.vue";
    import Operationsinformation from "@/tabs/Operation/Operationsinformation.vue";
    import PreanestesibedomningVisa from "@/tabs/Operation/PreanestesibedomningVisa.vue";
    import { statusar as operationStatuses } from "@/tabs/Operation/utils";
    import StatusAttHanteraIcon from "@/assets/actionIcons/statusatthantera.svg";
    import StatusWaitingIcon from "@/assets/actionIcons/statuswaiting.svg";
    import StatusKlaropIcon from "@/assets/actionIcons/statusklarop.svg";
    import StatusOffertforfraganskickadIcon from "@/assets/actionIcons/status_offertforfragan_skickad.svg";
    import StatusAvvaktanIcon from "@/assets/actionIcons/statusavvaktan.svg";
    import StatusBokadIcon from "@/assets/actionIcons/statusbokad.svg";
    import StatusAvbokadIcon from "@/assets/actionIcons/statusavbokad.svg";
    import StatusStrukenIcon from "@/assets/actionIcons/statusstruken.svg";
    import StatusPagaendeIcon from "@/assets/actionIcons/statuspagaende.svg";
    import StatusGenomfordIcon from "@/assets/actionIcons/statusgenomford.svg";
    import StatusSigneradIcon from "@/assets/sign_green.svg";
    import StatusPreanestesibedomningIcon from "@/assets/actionIcons/statuspreanestesibedomning.svg";
    import StatusPrioPreanestesibedomningIcon from "@/assets/actionIcons/status_prio_preanestesi.svg";
    import ActionIconOpanmalan from "@/assets/actionIcons/opanmalan.svg";
    import ActionIconSkapapreanestesibedomning from "@/assets/actionIcons/skapapreanestesibedomning.svg";
    import HuvudoperationskodAutocomplete from "@/components/forms/reusables/Huvudoperationskod/HuvudoperationskodAutocomplete.vue";
    import StatusRemissSkickadIcon from "@/assets/actionIcons/statusremissskickad.svg";

    const largeSearchWarning =
        "Du har valt ett stort urval som kan ta lång tid att ladda. Vill du gå vidare med din sökning?";

    const disabledButtonTooltip = "Tillämpa sökning först";

    const personIdColumn = {
        title: "PersonID",
        name: "patient_display.personId_formatted",
        lock: "left",
        sort: "patient__personId",
    };

    const tableColumns = [
        {
            title: "Opnummer",
            name: "opnr",
            sort: "opnr",
        },
        {
            title: "Enhet",
            name: "operationsenhet_display.alias",
            sort: "operationsenhet",
        },
        {
            title: "Opdatum",
            name: "current_bokad_operation_datum",
            sort: "current_bokad_operation_datum",
        },
        {
            title: "Huvudoperatör",
            name: "current_huvudoperator_display.user_display.name",
            sort: "current_huvudoperator",
        },
        {
            title: "Avtal",
            name: "avtal_display.namn",
            sort: "avtal",
        },
        {
            title: "Försäkringsbolag",
            name: "forsakringsbolag_display.namn",
            sort: "forsakringsbolag__namn",
        },
        {
            title: "Vårdform",
            name: "planerad_vardform_display.namn",
            sort: "planerad_vardform",
        },
        {
            title: "Kontaktorsak",
            name: "kontaktorsak_display.displayName",
            sort: "kontaktorsak__displayName",
        },
        {
            title: "Typoperation",
            name: "current_typoperation_display.namn",
            sort: "current_typoperation__namn",
        },
        {
            title: "Huvudopkod",
            name: "current_huvudoperationskod_display.code",
            sort: "current_huvudoperationskod__code",
        },
        {
            title: "Huvuddiagnoskod",
            name: "current_huvuddiagnos",
        },
        {
            title: "Nivåer",
            name: "current_antal_nivaer",
        },
        {
            title: "Opsal",
            name: "current_sal_display.namn",
            sort: "current_sal__ordning",
        },
        {
            title: "Optid planerad",
            name: "optid",
        },
        {
            title: "Optid genomförd",
            name: "optid_completed",
        },
        {
            title: "Optid differens",
            name: "optid_diff",
        },
        {
            title: "Förberedelsetid planerad",
            name: "forberedelser",
        },
        {
            title: "Förberedelsetid genomförd",
            name: "forberedelser_completed",
        },
        {
            title: "Förberedelsetid differens",
            name: "forberedelsetid_diff",
        },
        {
            title: "Avvecklingstid planerad",
            name: "avveckling",
        },
        {
            title: "Avvecklingstid genomförd",
            name: "avveckling_completed",
        },
        {
            title: "Avvecklingstid differens",
            name: "avveckling_diff",
        },
        {
            title: "Kommentar tider",
            name: "opjournal_sektion_tider_fritext",
        },
        {
            title: "Status",
            name: "status",
            type: "icon",
            icons: {
                "Att hantera": StatusAttHanteraIcon,
                "Väntar på preanestesibedömning": StatusPreanestesibedomningIcon,
                "Preanestesibedömning genomförd": StatusPreanestesibedomningIcon,
                "Prioriterad preanestesibedömning": StatusPrioPreanestesibedomningIcon,
                "Preanestesibedömning prio genomförd": StatusPrioPreanestesibedomningIcon,
                "Väntar på viktnedgång": StatusWaitingIcon,
                "Väntar på rökstopp": StatusWaitingIcon,
                "Väntar på prover/undersökning": StatusWaitingIcon,
                "Offertförfrågan skickad": StatusOffertforfraganskickadIcon,
                "Klar för operationsbokning": StatusKlaropIcon,
                "Patientvald väntan": StatusAvvaktanIcon,
                "Medicinsk väntan": StatusAvvaktanIcon,
                "Administrativ väntan": StatusAvvaktanIcon,
                Bokad: StatusBokadIcon,
                Avbokad: StatusAvbokadIcon,
                Struken: StatusStrukenIcon,
                Genomförd: StatusGenomfordIcon,
                Pågående: StatusPagaendeIcon,
                Signerad: StatusSigneradIcon,
                Skickad: StatusRemissSkickadIcon,
            },
            lock: "right",
            sort: "status__sortering",
        },
    ];

    const props = defineProps({
        tabId: Number,
    });

    const { tabId } = toRefs(props);

    const showSummary = ref(false);
    const isLoadingGenomfordData = ref(false);

    const buttonShouldBeDisabled = ref(false);

    const showTable = ref(false);

    const count = ref(0);
    const limit = ref(50);
    const offset = ref(0);
    const currentPage = ref(1);
    const sortField = ref("created_at");

    const filterValues = ref({});

    const selectedItem = ref(null);

    const operatorer = ref([]);
    const salar = ref([]);
    const typoperationer = ref([]);
    const vardformer = ref([]);
    const kontaktorsaker = ref([]);
    const operationsenheter = ref([]);
    const statusar = ref([]);
    const avtal = ref([]);
    const forsakringsbolag = ref([]);
    const anestesiologer = ref([]);

    const columns = ref(tableColumns);

    const dropdownActions = computed(() => {
        return [
            {
                title: "Visa operationsinformation",
                name: "visaOperationsinformation",
                icon: ActionIconOpanmalan,
                show: () =>
                    store.getters["ability/can"]({
                        action: "read",
                        subject: "anmalan",
                    }),
            },
            {
                title: "Visa preanestesibedömning",
                name: "visaPreanestesibedomning",
                icon: ActionIconSkapapreanestesibedomning,
                show: (item) =>
                    item.aktuell_preanestesibedomning &&
                    store.getters["ability/can"]({
                        action: "read",
                        subject: "preanestesibedomning",
                    }),
            },
        ];
    });

    const items = ref([]);

    const filterFields = computed(() => ({
        q: { label: "Sök" },
        date_from: { label: "Datum från " },
        date_to: { label: "Datum till " },
        operationsenhet: { label: "Enhet ", options: operationsenheter.value },
        planerad_vardform: { label: "Vårdform ", options: vardformer.value },
        kontaktorsak: { label: "Kontaktorsak ", options: kontaktorsaker.value },
        bokad_sal__id: { label: "Opsal ", options: salar.value },
        current_huvudoperator: { label: "Huvudoperatör ", options: operatorer.value },
        avtal: { label: "Avtal ", options: avtal.value },
        current_typoperation: { label: "Typoperation ", options: typoperationer.value },
        current_huvudoperationskod: { label: "Huvudoperationskod" },
        status: { label: "Status ", options: statusar.value },
        show_person_id: { label: "PersonID " },
        show_antal_nivaer_null: { label: "Inga nivåer" },
        antal_nivaer: { label: "Antal nivåer" },
        forsakringsbolag: { label: "Försäkringsbolag ", options: forsakringsbolag.value },
        anestesiolog: { label: "Anestesiolog ", options: anestesiologer.value },
    }));

    const operationsSummaryData = ref({
        summaryTableData: {
            forberedelse: { planerad_medelvarde: null, genomford_medelvarde: null, differens: null },
            operationstid: { planerad_medelvarde: null, genomford_medelvarde: null, differens: null },
            avveckling: { planerad_medelvarde: null, genomford_medelvarde: null, differens: null },
            total_tid_forberedelse_och_avveckling: {
                planerad_medelvarde: null,
                genomford_medelvarde: null,
                differens: null,
            },
        },
        totalOperations: null,
        totalOperationTime: null,
    });

    const handleFilterValuesUpdated = (updatedFilterValues) => {
        buttonShouldBeDisabled.value = !isEqual(updatedFilterValues, filterValues.value);
    };

    const showWarningSearchFilter = ({ action }) => {
        openDialog({
            type: "warning",
            description: largeSearchWarning,
            buttons: [
                {
                    title: "Avbryt",
                    type: "secondary",
                },
                {
                    title: "Ja, gå vidare",
                    type: "primary",
                    action,
                },
            ],
        });
    };

    const handleSearch = async (filterData) => {
        filterValues.value = filterData;

        if (Object.keys(filterData).length === 0) {
            /**
             * Visa varningsdialog om filtervärden är tomma.
             * För stor sökning — fråga användaren om de vill fortsätta.
             */
            showWarningSearchFilter({
                action: async () => {
                    await getSummary();
                    showTable.value = false;
                },
            });
        } else {
            await getSummary();
            showTable.value = false;
        }
    };

    async function loadPage(page) {
        currentPage.value = page;
        offset.value = (page - 1) * limit.value;
        await fetchOperationsStatistik();
    }

    async function changeLimit(value) {
        limit.value = value;
        await fetchOperationsStatistik();
    }

    async function sort(sort) {
        sortField.value = sort;
        await fetchOperationsStatistik();
    }

    async function downloadExcelFile() {
        store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true, ignoreTimeout: true });

        try {
            const jobResponse = await klinikenApi.post(
                "operation/statistik/excel/",
                null,
                getConfig({ params: { ...filterValues.value } })
            );

            const jobId = jobResponse.data.job_id;

            pollJob(
                `operation/statistik/excel/${jobId}/`,
                "Vill du fortsätta vänta på Excel-exporten?",
                async () => {
                    const response = await klinikenApi.get(
                        `operation/statistik/excel/${jobId}/`,
                        getConfig({ params: { download: "true" }, responseType: "blob" })
                    );
                    const contentDisposition = response.headers["content-disposition"];
                    const blob = new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.style.display = "none";
                    a.href = url;
                    a.download =
                        contentDisposition?.match(/filename="([^"]+)"/)?.[1] ?? "exportoperationsstatistik.xlsx";
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
                },
                (error) => {
                    store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
                    openDialog(typeof error === "string" ? error : "Kunde inte exportera Excel.", "warning");
                }
            );
        } catch (error) {
            store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
            openDialog(getErrorMessage(error), "error");
        }
    }

    const fetchOperationsStatistik = async () => {
        store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });

        try {
            const params = {
                ...filterValues.value,
                limit: limit.value,
                offset: offset.value,
                ordering: sortField.value,
            };

            const response = await klinikenApi.get(
                "/operation/statistik/",
                getConfig({
                    params,
                })
            );

            if (filterValues.value.show_person_id === true) {
                columns.value = [personIdColumn, ...tableColumns];
            } else {
                columns.value = tableColumns;
            }

            items.value = response.data.results;
            count.value = response.data.count;
        } catch (error) {
            openDialog("Kunde inte hämta operationsstatistik.", "warning");
        } finally {
            store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
            showTable.value = true;
        }
    };

    function tableRowClicked(item) {
        if (item.patient) {
            selectedItem.value = item;
            store.dispatch("patientData/loadFromBackend", {
                id: item.patient,
                failSilently: false,
            });
        }
    }

    function visaPreanestesibedomning(item) {
        store.dispatch("tabs/openTab", {
            component: PreanestesibedomningVisa,
            parameters: {
                preanestesibedomningId: item.aktuell_preanestesibedomning,
            },
        });
    }

    function visaOperationsinformation({ id: operationsanmalan_id }) {
        store.dispatch("tabs/openTab", {
            component: Operationsinformation,
            parameters: { operationsanmalan_id },
        });
    }

    const getHuvudoperator = async () => {
        try {
            const response = await klinikenApi.get(
                "core/profiles/",
                getConfig({
                    params: {
                        is_active: "true",
                        roll__code: "kirurg",
                        orgUnit__only_permitted: "true",
                    },
                })
            );
            /**
             * Anropar funktionen profileOptions för att bearbeta API-resultaten.
             * Med 'showUnit' satt till `true` visas operationsenheten tillsammans med profilens namn.
             *
             * @example
             * label: `${user_display.name}, ${orgUnit?.alias ?? orgUnit?.name}`,
             */
            operatorer.value = profileOptions(response.data.results, true);
        } catch (error) {
            openDialog("Kunde inte hämta huvudoperatörer.", "warning");
        }
    };

    const getSalar = async () => {
        try {
            const response = await klinikenApi.get("/operation/salar/", getConfig());

            salar.value = response.data.results.map(({ id, namn }) => {
                return {
                    value: `${id}`,
                    label: namn,
                };
            });
        } catch (error) {
            openDialog("Kunde inte hämta salar.", "warning");
        }
    };

    const getTypoperationer = async () => {
        try {
            const response = await klinikenApi.get("/operation/typoperationer/", getConfig());

            typoperationer.value = response.data.results.map(({ id, namn }) => {
                return {
                    value: `${id}`,
                    label: namn,
                };
            });
        } catch (error) {
            openDialog("Kunde inte hämta typoperationer.", "warning");
        }
    };

    const getOperationenheter = async () => {
        try {
            const response = await klinikenApi.get(
                "/core/orgunits/",
                getConfig({
                    params: {
                        is_active: "true",
                        vardenhetstyp: "operationsenhet",
                        only_permitted: "true",
                    },
                })
            );

            operationsenheter.value = response.data.results.map(({ id, alias }) => {
                return {
                    value: id,
                    label: alias,
                };
            });
        } catch (error) {
            openDialog("Kunde inte hämta operationsenheter.", "warning");
        }
    };

    const anmalanStatuses = computed(() => store.state.config?.operation?.OPERATION_ANMALAN_STATUSES);

    const getStatusar = async () => {
        if (anmalanStatuses.value && anmalanStatuses.value.length > 0) {
            statusar.value = operationStatuses.filter((status) => anmalanStatuses.value.includes(status.value));
        } else {
            statusar.value = [
                ...operationStatuses.map((status) => {
                    return { value: status.value, label: status.label };
                }),
            ];
        }
    };

    const getAvtal = async () => {
        try {
            const response = await klinikenApi.get("/core/avtal/", getConfig());

            avtal.value = response.data.map(({ id, namn }) => {
                return {
                    value: id,
                    label: namn,
                };
            });
        } catch (error) {
            openDialog("Kunde inte hämta avtal.", "warning");
        }
    };

    const getForsakringsbolag = async () => {
        try {
            const response = await klinikenApi.get("/core/forsakringsbolag/", getConfig());

            forsakringsbolag.value = response.data.results.map(({ id, namn }) => {
                return {
                    value: id,
                    label: namn,
                };
            });
        } catch (error) {
            openDialog("Kunde inte hämta forsakringsbolag.", "warning");
        }
    };

    const getVardformer = async () => {
        try {
            const { subsetName } = await store.dispatch("operation/vardform/fetchVardformer", {
                filterParams: {},
                displayMode: true,
            });

            const results = store.getters["operation/vardform/vardformSubset"]({ subsetName, displayMode: true });
            vardformer.value = [{ label: "", value: null }, ...(results || [])];
        } catch (error) {
            openDialog("Kunde inte hämta vårdformer.", "warning");
        }
    };

    const getKontaktorsaker = async () => {
        try {
            const { subsetName } = await store.dispatch("operation/kontaktorsak/fetchKontaktorsak", {
                filterParams: {},
                displayMode: true,
            });

            const results = store.getters["operation/kontaktorsak/kontaktorsakSubset"]({
                subsetName,
                displayMode: true,
            });
            kontaktorsaker.value = [
                { label: "", value: "" },
                ...(results || []).map(({ label, value }) => ({
                    label,
                    value: String(value),
                })),
            ];
        } catch (error) {
            openDialog("Kunde inte hämta kontaktorsaker.", "warning");
        }
    };

    const getAnestesiologer = async () => {
        try {
            const response = await klinikenApi.get(
                "core/profiles/",
                getConfig({
                    params: {
                        is_active: "true",
                        roll__code: "anestesilakare",
                    },
                })
            );

            anestesiologer.value = profileOptions(response.data.results, true);
        } catch (error) {
            openDialog("Kunde inte hämta anestesiologer.", "warning");
        }
    };

    const applyPlannedData = (data) => {
        operationsSummaryData.value.totalOperations = data.count;
        operationsSummaryData.value.totalOperationTime = data.total_optid;
        operationsSummaryData.value.summaryTableData.forberedelse.planerad_medelvarde = data.avg_forberedelsetid;
        operationsSummaryData.value.summaryTableData.operationstid.planerad_medelvarde = data.avg_optid;
        operationsSummaryData.value.summaryTableData.avveckling.planerad_medelvarde = data.avg_avvecklingstid;
        operationsSummaryData.value.summaryTableData.total_tid_forberedelse_och_avveckling.planerad_medelvarde =
            data.sum_avg_planned;
    };

    const applyCompletedData = (planned, completed) => {
        operationsSummaryData.value.summaryTableData.forberedelse.genomford_medelvarde =
            completed.avg_completed_forberedelser;
        operationsSummaryData.value.summaryTableData.operationstid.genomford_medelvarde = completed.avg_completed_optid;
        operationsSummaryData.value.summaryTableData.avveckling.genomford_medelvarde =
            completed.avg_completed_avveckling;
        operationsSummaryData.value.summaryTableData.total_tid_forberedelse_och_avveckling.genomford_medelvarde =
            completed.sum_avg_completed;

        if (planned.avg_forberedelsetid != null && planned.avg_optid != null && planned.avg_avvecklingstid != null) {
            const diff_forberedelser = -1 * (planned.avg_forberedelsetid - completed.avg_completed_forberedelser);
            const diff_optid = -1 * (planned.avg_optid - completed.avg_completed_optid);
            const diff_avveckling = -1 * (planned.avg_avvecklingstid - completed.avg_completed_avveckling);

            operationsSummaryData.value.summaryTableData.forberedelse.differens = diff_forberedelser;
            operationsSummaryData.value.summaryTableData.operationstid.differens = diff_optid;
            operationsSummaryData.value.summaryTableData.avveckling.differens = diff_avveckling;
            operationsSummaryData.value.summaryTableData.total_tid_forberedelse_och_avveckling.differens =
                diff_forberedelser + diff_optid + diff_avveckling;
        }
    };

    // Håller koll på aktiva poll-timers så att de kan avbrytas vid unmount.
    const pollTimerIds = ref(new Set());
    let pollCancelled = false;

    /**
     * Generisk Celery-jobbpollare.
     * Väntar 5 sekunder mellan varje anrop och kollar status på det asynkrona jobbet via den angivna pollUrl:en.
     * Var 30:e sekund frågas användaren om de vill fortsätta vänta.
     *
     * @param {string}   pollUrl    - relativ URL att GET:a för status
     * @param {string}   waitMsg    - meddelande som visas i 30-sekunders-dialogen
     * @param {function} onComplete - anropas med svaret när status === "completed"
     * @param {function} onError    - anropas med svaret när status === "failed" eller användaren avbryter
     * @param {number}   waitElapsed  - internt, spårar tid sedan senaste användarprompt
     */
    const pollJob = (pollUrl, waitMsg, onComplete, onError, waitElapsed = 0) => {
        const delay = 5000;
        const waitPromptInterval = 30000;

        const timerId = setTimeout(async () => {
            pollTimerIds.value.delete(timerId);

            if (pollCancelled) return;

            try {
                const response = await klinikenApi.get(pollUrl, getConfig());

                if (pollCancelled) return;

                if (response.data.status === "completed") {
                    // Await:ar onComplete för att fånga fel i asynkrona callbacks
                    // (t.ex. Excel-nedladdningen). Utan detta blir eventuella fel
                    // ohanterade och spinnaren kan fastna i aktivt läge.
                    try {
                        await Promise.resolve(onComplete(response.data.result));
                    } catch (completionError) {
                        onError(completionError);
                    }
                } else if (response.data.status === "failed") {
                    onError(response.data.error);
                } else {
                    const nextWait = waitElapsed + delay;
                    if (nextWait >= waitPromptInterval) {
                        openDialog({
                            type: "warning",
                            description: waitMsg,
                            buttons: [
                                {
                                    title: "Avbryt",
                                    type: "secondary",
                                    action: () => onError("Avbruten av användaren"),
                                },
                                {
                                    title: "Fortsätt vänta",
                                    type: "primary",
                                    action: () => pollJob(pollUrl, waitMsg, onComplete, onError, 0),
                                },
                            ],
                        });
                    } else {
                        pollJob(pollUrl, waitMsg, onComplete, onError, nextWait);
                    }
                }
            } catch (error) {
                if (!pollCancelled) onError(error);
            }
        }, delay);

        pollTimerIds.value.add(timerId);
    };

    onUnmounted(() => {
        pollCancelled = true;
        for (const id of pollTimerIds.value) {
            clearTimeout(id);
        }
        pollTimerIds.value.clear();

        // Återställ spinnaren och laddningsflaggan om polling avbryts vid navigering,
        // annars kan spinnaren fastna i aktivt läge tills nästa sidladdning.
        if (isLoadingGenomfordData.value) {
            isLoadingGenomfordData.value = false;
        }
        store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
    });

    const getSummary = async () => {
        store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });

        // Rensa alla inaktuella värden och signalera laddningsstatus inför varje ny sökning.
        isLoadingGenomfordData.value = true;
        operationsSummaryData.value.totalOperations = null;
        operationsSummaryData.value.totalOperationTime = null;
        const sd = operationsSummaryData.value.summaryTableData;
        for (const row of Object.values(sd)) {
            row.planerad_medelvarde = null;
            row.genomford_medelvarde = null;
            row.differens = null;
        }

        const params = { ...filterValues.value };

        try {
            // 1. Hämta planerad data och starta bakgrundsjobbet — båda skickas direkt.
            const [plannedResponse, jobResponse] = await Promise.all([
                klinikenApi.get("operation/statistik/summering/", getConfig({ params })),
                klinikenApi.post("operation/statistik/summering/genomford/", null, getConfig({ params })),
            ]);

            // Visa planerad data direkt.
            applyPlannedData(plannedResponse.data);
            showSummary.value = true;

            // 2. Polla tills Celery-uppgiften är klar och fyll sedan i genomförda kolumner.
            const jobId = jobResponse.data.job_id;
            pollJob(
                `operation/statistik/summering/genomford/${jobId}/`,
                "Vill du fortsätta vänta på beräkningen av statistik för genomförd?",
                (result) => {
                    applyCompletedData(plannedResponse.data, result);
                    isLoadingGenomfordData.value = false;
                    store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
                },
                () => {
                    isLoadingGenomfordData.value = false;
                    store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
                    openDialog("Kunde inte hämta genomförd statistik.", "warning");
                }
            );
        } catch (error) {
            isLoadingGenomfordData.value = false;
            openDialog("Kunde inte hämta summering.", "warning");
            store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
            showSummary.value = false;
        }
    };

    onMounted(async () => {
        /**
         * Hämta all nödvändig data asynkront parallellt, men vänta tills alla är klara innan vi fortsätter.
         * Parallellt innebär att alla hämtningar startar samtidigt och körs oberoende av varandra.
         * Detta görs för att minska den totala laddningstiden och förbättra prestandan.
         */
        await Promise.all([
            getHuvudoperator(),
            getSalar(),
            getTypoperationer(),
            getOperationenheter(),
            getAvtal(),
            getForsakringsbolag(),
            getVardformer(),
            getKontaktorsaker(),
            getAnestesiologer(),
        ]);

        getStatusar();
    });
</script>

<style lang="scss" scoped>
    @import "@/style/_variables.scss";

    .operationsstatistik {
        height: 100%;

        .header {
            display: flex;
            align-items: center;
            gap: 20px;
            margin-bottom: 26px;

            h1 {
                margin: 0;
            }
        }

        .subheader {
            display: flex;
            justify-content: space-between;
            margin-top: 20px;

            span {
                font-size: 22px;
                line-height: 30px;
                font-weight: normal;
            }

            strong {
                font-size: 22px;
                line-height: 30px;
                font-weight: normal;
                font-family:
                    Roboto Medium,
                    sans-serif;
            }
        }
    }
</style>
